import type { Order } from '../../types';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  order: Order | null;
  onVendorClick: (vendorName: string) => void;
  onInvoiceClick: (order: Order) => void;
  onTaxInvoiceClick: (order: Order) => void;
  onPaymentCompleteClick: (order: Order) => void;
  getOrderStatusColor: (status: string) => string;
}

const STAGES = ['발주서', '역발주', '인보이스', '결제', '세금계산서', '거래완료'];

export default function OrderDetailModal({
  isOpen, onClose, order, onVendorClick, onInvoiceClick, onTaxInvoiceClick, onPaymentCompleteClick, getOrderStatusColor
}: Props) {
  if (!isOpen || !order) return null;

  const currentStage = Math.ceil((order.progress / 100) * 6);
  const contractAmount = parseInt(order.amount.replace(/[^0-9]/g, '') || '0');
  const paidAmount = parseInt(order?.paidAmount || '0');
  const remaining = contractAmount - paidAmount;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] flex flex-col">

        {/* Header */}
        <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
              <i className="ri-file-list-3-line text-blue-600 text-lg"></i>
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">발주 상세</h2>
              <p className="text-xs text-gray-400">{order.id}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className={`px-3 py-1.5 rounded-full text-xs font-semibold ${getOrderStatusColor(order.status)}`}>
              {order.status}
            </span>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>
        </div>

        <div className="overflow-y-auto flex-1 p-6 space-y-5">

          {/* 진행 단계 */}
          <div className="flex items-center gap-1">
            {STAGES.map((name, i) => {
              const done = i < currentStage;
              const active = i === currentStage - 1;
              return (
                <div key={name} className="flex-1 flex flex-col items-center gap-1.5">
                  <div className={`w-full h-1.5 rounded-full ${done ? 'bg-blue-500' : 'bg-gray-200'}`}></div>
                  <span className={`text-[11px] ${active ? 'text-blue-600 font-semibold' : done ? 'text-blue-400' : 'text-gray-400'}`}>{name}</span>
                </div>
              );
            })}
          </div>

          {/* 단방향 발주 안내 */}
          {order.isExternalPartner && (
            <div className="bg-amber-50 border border-amber-100 rounded-xl p-3.5 flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <i className="ri-information-line text-amber-500"></i>
                <span className="text-sm text-amber-700">단방향 발주 (미초대 업체) — 업체관리에서 초대할 수 있습니다.</span>
              </div>
              <button
                onClick={() => { onClose(); onVendorClick(order.vendor); }}
                className="px-3 py-1.5 bg-amber-500 text-white text-xs rounded-lg hover:bg-amber-600 flex items-center gap-1"
              >
                <i className="ri-building-line text-xs"></i> 업체 상세
              </button>
            </div>
          )}

          {/* 업체 + 고객 정보 (2컬럼) */}
          <div className="grid grid-cols-2 gap-4">
            <div className="rounded-xl border border-gray-100 p-4">
              <div className="flex items-center gap-2 mb-3">
                <i className="ri-building-line text-gray-400"></i>
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">업체 정보</span>
              </div>
              <div className="space-y-2.5">
                <div>
                  <span className="text-[11px] text-gray-400">업체명</span>
                  <p className="text-sm font-semibold text-blue-600 hover:text-blue-700 cursor-pointer" onClick={() => { onClose(); onVendorClick(order.vendor); }}>{order.vendor}</p>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div><span className="text-[11px] text-gray-400">담당자</span><p className="text-sm text-gray-800">{order.manager}</p></div>
                  <div><span className="text-[11px] text-gray-400">서비스</span><p className="text-sm text-gray-800">{order.service}</p></div>
                </div>
                <div><span className="text-[11px] text-gray-400">발주일</span><p className="text-sm text-gray-800">{order.orderDate}</p></div>
              </div>
            </div>

            <div className="rounded-xl border border-gray-100 p-4">
              <div className="flex items-center gap-2 mb-3">
                <i className="ri-heart-line text-gray-400"></i>
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">고객 정보</span>
              </div>
              <div className="space-y-2.5">
                <div className="grid grid-cols-2 gap-2">
                  <div><span className="text-[11px] text-gray-400">고객명</span><p className="text-sm font-medium text-gray-800">{order.customer}</p></div>
                  <div><span className="text-[11px] text-gray-400">연락처</span><p className="text-sm text-gray-800">{order.phone}</p></div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div><span className="text-[11px] text-gray-400">예식일</span><p className="text-sm text-gray-800">{order.weddingDate}</p></div>
                  <div><span className="text-[11px] text-gray-400">예식장</span><p className="text-sm text-gray-800">{order.venue}</p></div>
                </div>
              </div>
            </div>
          </div>

          {/* 금액 정보 */}
          <div className="grid grid-cols-3 gap-3">
            <div className="rounded-xl border border-gray-100 p-4 text-center">
              <span className="text-[11px] text-gray-400">계약금액</span>
              <p className="text-xl font-bold text-gray-900 mt-1">{contractAmount.toLocaleString()}<span className="text-sm font-normal text-gray-500">원</span></p>
            </div>
            <div className="rounded-xl border border-gray-100 p-4 text-center">
              <span className="text-[11px] text-gray-400">결제완료</span>
              <p className="text-xl font-bold text-green-600 mt-1">{paidAmount.toLocaleString()}<span className="text-sm font-normal text-gray-500">원</span></p>
            </div>
            <div className="rounded-xl border border-gray-100 p-4 text-center">
              <span className="text-[11px] text-gray-400">잔여금액</span>
              <p className="text-xl font-bold text-red-500 mt-1">{remaining.toLocaleString()}<span className="text-sm font-normal text-gray-500">원</span></p>
            </div>
          </div>

          {/* 문서 정보 */}
          {(order.invoiceNumber || order.taxInvoiceNumber) && (
            <div>
              <div className="flex items-center gap-2 mb-3">
                <i className="ri-file-text-line text-gray-400"></i>
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">문서</span>
              </div>
              <div className="flex gap-3">
                {order.invoiceNumber && (
                  <button onClick={() => { onClose(); onInvoiceClick(order); }}
                    className="flex-1 flex items-center justify-between px-4 py-3 rounded-xl border border-gray-100 hover:border-purple-200 hover:bg-purple-50 transition-all group">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center"><i className="ri-file-text-line text-purple-600 text-sm"></i></div>
                      <div className="text-left"><p className="text-sm font-medium text-gray-800">인보이스</p><p className="text-[11px] text-gray-400">{order.invoiceNumber}</p></div>
                    </div>
                    <i className="ri-arrow-right-s-line text-gray-300 group-hover:text-purple-500"></i>
                  </button>
                )}
                {order.taxInvoiceNumber && (
                  <button onClick={() => { onClose(); onTaxInvoiceClick(order); }}
                    className="flex-1 flex items-center justify-between px-4 py-3 rounded-xl border border-gray-100 hover:border-indigo-200 hover:bg-indigo-50 transition-all group">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center"><i className="ri-file-list-line text-indigo-600 text-sm"></i></div>
                      <div className="text-left"><p className="text-sm font-medium text-gray-800">세금계산서</p><p className="text-[11px] text-gray-400">{order.taxInvoiceNumber}</p></div>
                    </div>
                    <i className="ri-arrow-right-s-line text-gray-300 group-hover:text-indigo-500"></i>
                  </button>
                )}
              </div>
            </div>
          )}

          {/* 메모 */}
          {order.notes && (
            <div className="rounded-xl border border-gray-100 p-4">
              <div className="flex items-center gap-2 mb-2">
                <i className="ri-sticky-note-line text-gray-400"></i>
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">메모</span>
              </div>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{order.notes}</p>
            </div>
          )}

          {/* 액션 버튼 */}
          {(order.status === '역발주' || order.status === '세금계산서') && (
            <div className="flex gap-3">
              {order.status === '역발주' && (
                <>
                  <button onClick={() => { onClose(); onInvoiceClick(order); }}
                    className="flex-1 py-2.5 bg-purple-600 text-white rounded-xl hover:bg-purple-700 text-sm font-medium flex items-center justify-center gap-1.5">
                    <i className="ri-file-text-line"></i> 인보이스 표시
                  </button>
                  <button onClick={() => { onClose(); onPaymentCompleteClick(order); }}
                    className="flex-1 py-2.5 bg-green-600 text-white rounded-xl hover:bg-green-700 text-sm font-medium flex items-center justify-center gap-1.5">
                    <i className="ri-check-line"></i> 결제완료
                  </button>
                </>
              )}
              {order.status === '세금계산서' && (
                <button onClick={() => { onClose(); onTaxInvoiceClick(order); }}
                  className="flex-1 py-2.5 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 text-sm font-medium flex items-center justify-center gap-1.5">
                  <i className="ri-file-list-line"></i> 세금계산서 표시
                </button>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 flex justify-end">
          <button onClick={onClose} className="px-5 py-2.5 text-sm text-gray-600 bg-white border border-gray-200 rounded-xl hover:bg-gray-50">닫기</button>
        </div>
      </div>
    </div>
  );
}
